import { IconButton, Spinner, Stack, Tooltip, TooltipContent, TooltipTrigger } from '@landler/component-library';
import { useTranslation } from 'react-i18next';

import { IndicatorFactType } from '@/api/rest/resources/types/fact';
import { PlotGroup } from '@/api/rest/resources/types/plotGroup';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { SuspenseBoundary } from '@/components';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { DetailsCard } from '@/components/DetailsCard/DetailsCard';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { isNullish } from '@/utils/booleans';

import {
  availableGraphsToIndicatorNames,
  INDICATORS_WITH_GRAPH_TYPE_TOGGLE,
  INDICATORS_WITH_PER_HA_TOGGLE,
  INDICATORS_WITH_ZOOM,
} from '../constants';

const HideIndicatorButton = ({
  graphTileType,
  columnKey,
  onHideIndicator,
}: {
  graphTileType: GraphTileType;
  columnKey: string;
  onHideIndicator: (indicator: IndicatorFactType) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <IconButton
          className='text-text-secondary hidden group-hover/indicator:block py-2 px-3'
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onHideIndicator(
              availableGraphsToIndicatorNames[
                `${graphTileType as string}` as keyof typeof availableGraphsToIndicatorNames
              ],
            );
          }}
          data-testid={`hide-indicator-${columnKey}-${graphTileType}`}
        >
          <Icon icon='ri-eye-off-line' />
        </IconButton>
      </TooltipTrigger>
      <TooltipContent className='whitespace-pre-wrap'>
        {t('global.compare.graphs.hideIndicators.helper')}
      </TooltipContent>
    </Tooltip>
  );
};

export const SingleIndicatorCard = ({
  graphTileType,
  group,
  columnKey,
  index,
  onHideIndicator,
}: {
  graphTileType: GraphTileType;
  group: PlotGroup | null;
  columnKey: string;
  index: number;
  onHideIndicator: (indicator: IndicatorFactType) => void;
}) => {
  const { t } = useTranslation();

  const hasGrouping = !isNullish(group)
    ? group.groupBy && Object.keys(group.groupBy).length > 0 && Object.values(group.groupBy).some((v) => v !== null)
    : false;

  const props = {
    headerProps: {
      title: t(`global.compare.graphs.indicators.${graphTileType as keyof typeof availableGraphsToIndicatorNames}`),
      showToggle: !!INDICATORS_WITH_PER_HA_TOGGLE.includes(graphTileType),
      showZoom: !!INDICATORS_WITH_ZOOM.includes(graphTileType),
      showGraphType: !!INDICATORS_WITH_GRAPH_TYPE_TOGGLE.includes(graphTileType) && !hasGrouping,
    },
    graphTileType,
    graphTileProps: {
      headerProps: { title: '' },
      contentProps: { graphFactType: null, data: [], chartConfig: [] },
      plotGroupId: group?.id ?? '',
      // MVP-7133 - disable for non-applicable graphs (waiting on Product for report feedback)
      showEmpty: true,
      plotRenderType: NUM_ITEMS_TYPE.TOP_PLOTS,
    },
  };

  return (
    <Stack className='flex-row max-h-[680px] group/indicator items-center gap-2'>
      <SuspenseBoundary
        fallback={
          <Stack className='items-center justify-center w-full self-start'>
            <Spinner size={10} />
          </Stack>
        }
      >
        {columnKey.includes('left') && (
          <HideIndicatorButton graphTileType={graphTileType} columnKey={columnKey} onHideIndicator={onHideIndicator} />
        )}
        <Stack className='flex-1 min-w-0'>
          <DetailsCard key={`details-card-${columnKey}-${index}`} {...props} />
        </Stack>
        {columnKey.includes('right') && (
          <HideIndicatorButton graphTileType={graphTileType} columnKey={columnKey} onHideIndicator={onHideIndicator} />
        )}
      </SuspenseBoundary>
    </Stack>
  );
};
